// src/pages/CheckoutPage.tsx - VERSION COMPLÈTE avec frais de livraison détaillés
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import cartService, { CartItem, DeliveryAddress, CheckoutRequest } from '../services/cartService';
import productService from '../services/productService';
import orderService from '../services/orderService';
import { 
  ArrowLeft, 
  ShoppingCart, 
  CreditCard,
  MapPin,
  Phone,
  Mail,
  User,
  Truck,
  Shield,
  CheckCircle,
  AlertCircle,
  Clock,
  Package,
  Copy,
  ExternalLink,
  Smartphone,
  Info
} from 'lucide-react';

interface PaymentMethod {
  type: 'cash' | 'mobile_money' | 'card';
  label: string;
  description: string;
  icon: any;
}

// ✅ NOUVEAU - Interface pour les infos de paiement farmer
interface OrderPaymentInfo {
  order_id: number;
  total_amount: number;
  farmer_name: string;
  farmer_merchant_phone: string;
  farmer_phone?: string;
  payment_instructions: string;
  order_status: string;
}

const CheckoutPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingOrder, setProcessingOrder] = useState(false);
  const [error, setError] = useState('');
  
  // ✅ MODIFIÉ - Maintenant 4 étapes (ajout paiement farmer)
  const [currentStep, setCurrentStep] = useState(1);
  
  // ✅ NOUVEAU - État pour les infos de paiement farmer
  const [paymentInfo, setPaymentInfo] = useState<OrderPaymentInfo | null>(null);
  const [createdOrder, setCreatedOrder] = useState<any>(null);
  
  // États existants
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress>({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    additionalInfo: ''
  });
  
  const [selectedPayment, setSelectedPayment] = useState<'cash' | 'mobile_money' | 'card'>('mobile_money');
  
  // ✅ MODIFIÉ - Mettre mobile_money par défaut car c'est le processus principal
  const paymentMethods: PaymentMethod[] = [
    {
      type: 'mobile_money',
      label: 'Mobile Money (MTN/Moov)',
      description: 'Paiement direct au producteur via MTN MoMo ou Moov Money',
      icon: Smartphone
    },
    {
      type: 'cash',
      label: 'Paiement à la livraison',
      description: 'Payez en espèces lors de la réception',
      icon: Package
    },
    {
      type: 'card',
      label: 'Carte bancaire',
      description: 'Visa, Mastercard (bientôt disponible)',
      icon: CreditCard
    }
  ];

  useEffect(() => {
    if (!user?.is_customer) {
      navigate('/login');
      return;
    }
    
    loadCart();
    loadSavedCheckoutData();
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      setDeliveryAddress(prev => ({
        ...prev,
        fullName: prev.fullName || user.name || '',
        phone: prev.phone || user.phone || '',
        email: prev.email || user.email || ''
      }));
    }
  }, [user]);

  const loadCart = async () => {
    try {
      setLoading(true);
      setError('');
      const cartData = await cartService.getCart();
      
      if (cartData.length === 0) {
        navigate('/cart');
        return;
      }
      
      setCartItems(cartData);
    } catch (error) {
      console.error('Erreur lors du chargement du panier:', error);
      setError('Erreur lors du chargement du panier.');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedCheckoutData = () => {
    const savedData = cartService.getCheckoutData();
    if (savedData) {
      setDeliveryAddress(savedData.delivery_address);
      setSelectedPayment(savedData.payment_method);
    }
  };

  const saveCheckoutData = () => {
    const checkoutData: CheckoutRequest = {
      delivery_address: deliveryAddress,
      payment_method: selectedPayment,
      delivery_instructions: deliveryAddress.additionalInfo
    };
    cartService.saveCheckoutData(checkoutData);
  };

  const validateDeliveryAddress = (): string[] => {
    return cartService.validateDeliveryAddress(deliveryAddress);
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      const errors = validateDeliveryAddress();
      if (errors.length > 0) {
        setError(errors.join(', '));
        return;
      }
    }
    
    if (currentStep === 2) {
      if (!selectedPayment) {
        setError('Veuillez sélectionner un mode de paiement');
        return;
      }
    }
    
    setError('');
    saveCheckoutData();
    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setError('');
      setCurrentStep(currentStep - 1);
    }
  };

  // ✅ NOUVEAU - Processus de création de commande avec paiement farmer
  const handlePlaceOrder = async () => {
    const addressErrors = validateDeliveryAddress();
    if (addressErrors.length > 0) {
      setError(addressErrors.join(', '));
      return;
    }

    const { canCheckout, errors } = cartService.canCheckout(cartItems);
    if (!canCheckout) {
      setError(errors.join(', '));
      return;
    }

    if (!selectedPayment) {
      setError('Veuillez sélectionner un mode de paiement');
      return;
    }

    const finalTotal = cartService.calculateFinalTotal(cartItems, deliveryFee);
    if (!confirm(`Confirmer la commande de ${cartService.formatPrice(finalTotal)} ?`)) {
      return;
    }

    try {
      setProcessingOrder(true);
      setError('');
      
      // ✅ 1. Créer la commande (status: payment_pending)
      const checkoutData: CheckoutRequest = {
        delivery_address: deliveryAddress,
        payment_method: selectedPayment,
        delivery_instructions: deliveryAddress.additionalInfo
      };
      
      console.log('🚀 Création de la commande...', checkoutData);
      const order = await cartService.checkoutWithDetails(checkoutData);
      setCreatedOrder(order);
      
      // ✅ 2. Si paiement mobile_money, récupérer les infos farmer
      if (selectedPayment === 'mobile_money') {
        console.log('💰 Récupération infos paiement farmer...');
        const paymentData = await orderService.getOrderPaymentInfo(order.id);
        setPaymentInfo(paymentData);
        
        // Passer à l'étape 4 : affichage paiement farmer
        setCurrentStep(4);
      } else {
        // Pour cash ou card, redirection classique
        // ✅ CORRIGÉ - Pour cash ou card, redirection vers dashboard aussi
        cartService.clearCheckoutData();
        navigate('/customer-dashboard', {
          replace: true,
          state: {
            orderSuccess: true,
            orderId: order.id,
            message: `Commande #${order.id} créée avec succès !`,
            paymentMethod: selectedPayment
          }
        });
      }
      
    } catch (error) {
      console.error('❌ Erreur lors de la commande:', error);
      const detail = error?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erreur lors de la passation de commande. Veuillez réessayer.');
    } finally {
      setProcessingOrder(false);
    }
  };

  // ✅ NOUVEAU - Copier numéro dans le presse-papier
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Numéro copié dans le presse-papier !');
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  // ✅ NOUVEAU - Continuer vers la confirmation
  const handleContinueToConfirmation = () => {
    cartService.clearCheckoutData();
    
    // ✅ NOUVEAU - Redirection directe vers customer dashboard
    navigate('/customer-dashboard', {
      replace: true, // Empêche de revenir en arrière vers le checkout
      state: {
        orderSuccess: true,
        orderId: createdOrder?.id,
        message: `Commande #${createdOrder?.id} créée avec succès !`
      }
    });
  };

  const handleAddressChange = (field: keyof DeliveryAddress, value: string) => {
    setDeliveryAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ✅ NOUVEAU - Calculs détaillés avec frais de livraison
  const cartSummary = cartService.getCartSummary(cartItems);
  const cartTotal = cartSummary.totalNegotiated;
  const itemsCount = cartSummary.itemsCount;
  const deliveryFee = cartSummary.deliveryFee;
  const finalTotal = cartSummary.finalTotal;
  const deliveryBreakdown = cartService.getDeliveryFeeBreakdown(cartItems);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
          <p className="mt-2">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-beige min-h-screen">
      {/* Header avec navigation */}
      <div className="flex items-center gap-4 mb-6">
        <Link 
          to="/cart"
          className="flex items-center gap-2 text-slate hover:text-terracotta transition duration-300"
        >
          <ArrowLeft className="h-5 w-5" />
          Retour au panier
        </Link>
      </div>

      <div className="flex items-center gap-3 mb-8">
        <CreditCard className="h-8 w-8 text-olive" />
        <h1 className="text-3xl font-bold text-slate">Finaliser ma commande</h1>
      </div>

      {/* ✅ MODIFIÉ - Indicateur de progression 4 étapes */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-2">
            {/* Étape 1: Livraison */}
            <div className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-olive' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 1 ? 'bg-olive text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                {currentStep > 1 ? <CheckCircle className="h-5 w-5" /> : '1'}
              </div>
              <span className="text-sm font-medium">Livraison</span>
            </div>
            
            <div className={`w-6 h-0.5 ${currentStep >= 2 ? 'bg-olive' : 'bg-gray-300'}`}></div>
            
            {/* Étape 2: Paiement */}
            <div className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-olive' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 2 ? 'bg-olive text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                {currentStep > 2 ? <CheckCircle className="h-5 w-5" /> : '2'}
              </div>
              <span className="text-sm font-medium">Paiement</span>
            </div>
            
            <div className={`w-6 h-0.5 ${currentStep >= 3 ? 'bg-olive' : 'bg-gray-300'}`}></div>
            
            {/* Étape 3: Confirmation */}
            <div className={`flex items-center gap-2 ${currentStep >= 3 ? 'text-olive' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 3 ? 'bg-olive text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                {currentStep > 3 ? <CheckCircle className="h-5 w-5" /> : '3'}
              </div>
              <span className="text-sm font-medium">Confirmation</span>
            </div>
            
            {/* ✅ NOUVEAU - Étape 4: Paiement Farmer */}
            {selectedPayment === 'mobile_money' && (
              <>
                <div className={`w-6 h-0.5 ${currentStep >= 4 ? 'bg-olive' : 'bg-gray-300'}`}></div>
                <div className={`flex items-center gap-2 ${currentStep >= 4 ? 'text-olive' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= 4 ? 'bg-olive text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {currentStep > 4 ? <CheckCircle className="h-5 w-5" /> : '4'}
                  </div>
                  <span className="text-sm font-medium">Paiement</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Contenu principal - Étapes */}
        <div className="lg:col-span-2">
          
          {/* ÉTAPE 1: ADRESSE DE LIVRAISON */}
          {currentStep === 1 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="h-6 w-6 text-olive" />
                <h2 className="text-xl font-semibold text-slate">Adresse de livraison</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    value={deliveryAddress.fullName}
                    onChange={(e) => handleAddressChange('fullName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                    placeholder="Votre nom complet"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    value={deliveryAddress.phone}
                    onChange={(e) => handleAddressChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                    placeholder="+229 01 95 04 08 02"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={deliveryAddress.email}
                    onChange={(e) => handleAddressChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                    placeholder="votre.email@exemple.com"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate mb-2">
                    Adresse complète *
                  </label>
                  <textarea
                    value={deliveryAddress.address}
                    onChange={(e) => handleAddressChange('address', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                    placeholder="Rue, quartier, numéro de maison..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate mb-2">
                    Ville *
                  </label>
                  <input
                    type="text"
                    value={deliveryAddress.city}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                    placeholder="Cotonou, Porto-Novo..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate mb-2">
                    Code postal
                  </label>
                  <input
                    type="text"
                    value={deliveryAddress.postalCode}
                    onChange={(e) => handleAddressChange('postalCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                    placeholder="01 BP 123"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate mb-2">
                    Instructions de livraison
                  </label>
                  <textarea
                    value={deliveryAddress.additionalInfo}
                    onChange={(e) => handleAddressChange('additionalInfo', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-olive"
                    placeholder="Instructions spéciales, point de repère..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleNextStep}
                  className="bg-olive text-white px-6 py-2 rounded-md hover:bg-terracotta transition duration-300"
                >
                  Continuer vers le paiement
                </button>
              </div>
            </div>
          )}

          {/* ÉTAPE 2: MODE DE PAIEMENT */}
          {currentStep === 2 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="h-6 w-6 text-olive" />
                <h2 className="text-xl font-semibold text-slate">Mode de paiement</h2>
              </div>

              {/* ✅ NOUVEAU - Mise en avant du mobile money */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <Smartphone className="h-6 w-6 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-blue-900">Recommandé : Faite appel à BeninAgri</h3>
                    <p className="text-sm text-blue-700">
                       Nous prenons contact avec le producteur en votre nom, sécurisons la transaction 
                       et assurons le suivi jusqu'à la livraison. Une solution fiable pour acheter 
                       l'esprit tranquille.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {paymentMethods.map((method) => (
                  <label
                    key={method.type}
                    className={`block p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedPayment === method.type
                        ? 'border-olive bg-olive bg-opacity-5'
                        : 'border-gray-300 hover:border-gray-400'
                    } ${method.type === 'card' ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-4">
                      <input
                        type="radio"
                        name="payment"
                        value={method.type}
                        checked={selectedPayment === method.type}
                        onChange={(e) => setSelectedPayment(e.target.value as any)}
                        disabled={method.type === 'card'}
                        className="text-olive focus:ring-olive"
                      />
                      <method.icon className="h-6 w-6 text-olive" />
                      <div>
                        <div className="font-medium text-slate">
                          {method.label}
                          {method.type === 'card' && (
                            <span className="text-sm text-gray-500 ml-2">(Bientôt)</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">{method.description}</div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Bloc OYE-Consulting */}
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">🤝</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 mb-1">Besoin d'aide ? BeninAgri à votre service</h3>
                    <p className="text-sm text-amber-800 mb-3">
                      Livraison, sourcing, représentation auprès du vendeur... Nous nous occupons de tout selon vos besoins.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <a href="https://wa.me/22901095040802" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition duration-300 text-sm">
                        WhatsApp OYE
                      </a>
                      <a href="mailto:contact@beninagri.com" className="flex items-center justify-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 transition duration-300 text-sm">
                        Email OYE
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <button
                  onClick={handlePreviousStep}
                  className="bg-gray-200 text-slate px-6 py-2 rounded-md hover:bg-gray-300 transition duration-300"
                >
                  Retour
                </button>
                <button
                  onClick={handleNextStep}
                  className="bg-olive text-white px-6 py-2 rounded-md hover:bg-terracotta transition duration-300"
                >
                  Confirmer le paiement
                </button>
              </div>
            </div>
          )}

          {/* ÉTAPE 3: CONFIRMATION FINALE */}
          {currentStep === 3 && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="h-6 w-6 text-olive" />
                <h2 className="text-xl font-semibold text-slate">Confirmation de commande</h2>
              </div>

              {/* Résumé adresse de livraison */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-slate mb-2 flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Adresse de livraison
                </h3>
                <div className="text-sm text-gray-600">
                  <p className="font-medium">{deliveryAddress.fullName}</p>
                  <p>{deliveryAddress.phone}</p>
                  <p>{deliveryAddress.email}</p>
                  <p>{deliveryAddress.address}</p>
                  <p>{deliveryAddress.city} {deliveryAddress.postalCode}</p>
                  {deliveryAddress.additionalInfo && (
                    <p className="italic mt-1">{deliveryAddress.additionalInfo}</p>
                  )}
                </div>
              </div>

              {/* Résumé mode de paiement */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-slate mb-2 flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Mode de paiement
                </h3>
                <div className="text-sm text-gray-600">
                  {cartService.getPaymentMethodLabel(selectedPayment)}
                </div>
              </div>

              {/* ✅ NOUVEAU - Résumé détaillé des frais */}
              {deliveryFee > 0 && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                    <Truck className="h-5 w-5" />
                    Détail des frais de livraison
                  </h3>
                  <div className="space-y-2 text-sm">
                    {deliveryBreakdown.breakdown.filter(item => !item.isFree).map((item, index) => (
                      <div key={index} className="flex justify-between text-blue-800">
                        <span>• {item.productName}</span>
                        <span className="font-medium">{cartService.formatPrice(item.fee)}</span>
                      </div>
                    ))}
                    <div className="border-t border-blue-200 pt-2 flex justify-between font-medium text-blue-900">
                      <span>Total frais de livraison:</span>
                      <span>{cartService.formatPrice(deliveryFee)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ✅ NOUVEAU - Avertissement pour mobile money */}
              {selectedPayment === 'mobile_money' && (
                <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-orange-600" />
                    <h3 className="font-medium text-orange-900">Information importante</h3>
                  </div>
                  <p className="text-sm text-orange-800">
                    Après validation, vous recevrez les informations de paiement du producteur. 
                    Vous devrez effectuer le paiement MTN MoMo ou Moov Money directement.
                  </p>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={handlePreviousStep}
                  className="bg-gray-200 text-slate px-6 py-2 rounded-md hover:bg-gray-300 transition duration-300"
                >
                  Retour
                </button>
                <button
                  onClick={handlePlaceOrder}
                  disabled={processingOrder}
                  className="bg-olive text-white px-6 py-2 rounded-md hover:bg-terracotta transition duration-300 disabled:opacity-50 flex items-center gap-2"
                >
                  {processingOrder ? (
                    <>
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Traitement...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      Finaliser la commande
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ✅ NOUVEAU - ÉTAPE 4: PAIEMENT FARMER */}
          {currentStep === 4 && paymentInfo && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-6">
                <Smartphone className="h-6 w-6 text-olive" />
                <h2 className="text-xl font-semibold text-slate">Paiement au producteur</h2>
              </div>

              {/* Succès de création de commande */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-medium text-green-900">Commande créée avec succès !</h3>
                </div>
                <p className="text-sm text-green-800">
                  Commande N°{paymentInfo.order_id} - Montant: {cartService.formatPrice(paymentInfo.total_amount)}
                </p>
              </div>

              {/* Informations de paiement */}
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-3">
                    Informations de paiement
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">Producteur:</span>
                      <span className="font-medium text-blue-900">{paymentInfo.farmer_name}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-700">Montant à payer:</span>
                      <span className="font-bold text-lg text-blue-900">
                        {cartService.formatPrice(paymentInfo.total_amount)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center bg-white rounded p-3">
                      <div>
                        <span className="text-sm text-blue-700">Numéro de paiement:</span>
                        <div className="font-mono text-lg font-bold text-blue-900">
                          {paymentInfo.farmer_merchant_phone}
                        </div>
                      </div>
                      <button
                        onClick={() => copyToClipboard(paymentInfo.farmer_merchant_phone)}
                        className="bg-blue-100 hover:bg-blue-200 text-blue-700 p-2 rounded transition duration-300"
                        title="Copier le numéro"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Instructions détaillées */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate mb-3">Instructions de paiement</h4>
                  <div className="text-sm text-gray-700 whitespace-pre-line">
                    {paymentInfo.payment_instructions}
                  </div>
                </div>

                {/* Avertissement sécurité */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-5 w-5 text-red-600" />
                    <h4 className="font-medium text-red-900">Sécurité</h4>
                  </div>
                  <p className="text-sm text-red-800">
                    ⚠️ Attention aux arnaqueurs ! Payez UNIQUEMENT le montant exact au numéro affiché. 
                    {paymentInfo.farmer_phone && (
                      <> En cas de doute, contactez le producteur au {paymentInfo.farmer_phone}.</>
                    )}
                    {!paymentInfo.farmer_phone && (
                      <> En cas de doute, contactez l'administrateur.</>
                    )}
                  </p>
                </div>

                {/* Statut de la commande */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-5 w-5 text-yellow-600" />
                    <h4 className="font-medium text-yellow-900">Statut actuel</h4>
                  </div>
                  <p className="text-sm text-yellow-800">
                    Votre commande est en attente de confirmation de paiement par le producteur.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  onClick={handleContinueToConfirmation}
                  className="bg-olive text-white px-8 py-3 rounded-md hover:bg-terracotta transition duration-300 flex items-center gap-2"
                >
                  <ExternalLink className="h-5 w-5" />
                  Continuer vers le suivi de commande
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ✅ MODIFIÉ - SIDEBAR AVEC FRAIS DE LIVRAISON DÉTAILLÉS */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
            <h2 className="text-lg font-semibold text-slate mb-4">Résumé de commande</h2>
            
            {/* Articles du panier */}
            <div className="space-y-3 mb-4">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-gray-100 rounded flex-shrink-0">
                    {item.product.thumbnail_url && (
                      <img 
                        src={productService.getImageUrl(item.product.thumbnail_url)} 
                        alt={item.product.name}
                        className="h-full w-full object-cover rounded"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate truncate">
                      {item.product.name}
                    </p>
                    <p className="text-xs text-gray-600">
                      {item.quantity} x {cartService.formatPrice(cartService.getEffectivePrice(item))}
                    </p>
                    {/* ✅ NOUVEAU - Afficher frais de livraison par produit */}
                    {(item.product.delivery_fee || 0) > 0 && (
                      <p className="text-xs text-blue-600">
                        + Livraison: {cartService.formatPrice(item.product.delivery_fee || 0)}
                      </p>
                    )}
                  </div>
                  <div className="text-sm font-medium text-slate">
                    {cartService.formatPrice(cartService.getItemTotalCost(item).totalForQuantity)}
                  </div>
                </div>
              ))}
            </div>

            {/* ✅ NOUVEAU - Calculs finaux détaillés avec frais de livraison */}
            <div className="space-y-2 border-t border-gray-200 pt-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Sous-total ({itemsCount} article{itemsCount !== 1 ? 's' : ''})</span>
                <span className="font-medium">{cartService.formatPrice(cartTotal)}</span>
              </div>
              
              {/* ✅ NOUVEAU - Détail des frais de livraison */}
              <div className="flex justify-between text-sm">
                <div className="text-gray-600">
                  <span>Livraison</span>
                  {deliveryFee > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      {cartItems.map((item, index) => {
                        const itemFee = item.product.delivery_fee || 0;
                        if (itemFee > 0) {
                          return (
                            <div key={index}>
                              • {item.product.name}: {cartService.formatPrice(itemFee)}
                            </div>
                          );
                        }
                        return null;
                      }).filter(Boolean)}
                    </div>
                  )}
                </div>
                <span className={`font-medium ${deliveryFee > 0 ? 'text-blue-600' : 'text-green-600'}`}>
                  {deliveryFee > 0 ? cartService.formatPrice(deliveryFee) : 'Gratuite'}
                </span>
              </div>

              {/* ✅ NOUVEAU - Affichage des économies si négociations */}
              {cartSummary.hasNegotiations && cartSummary.totalSavings > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Économies (négociations)</span>
                  <span className="font-medium">-{cartService.formatPrice(cartSummary.totalSavings)}</span>
                </div>
              )}
              
              <div className="flex justify-between text-lg font-semibold border-t border-gray-200 pt-2">
                <span className="text-slate">Total</span>
                <span className="text-terracotta">{cartService.formatPrice(finalTotal)}</span>
              </div>
            </div>

            {/* ✅ NOUVEAU - Informations sur les frais */}
            <div className="mt-4 space-y-2">
              {deliveryFee > 0 && (
                <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 p-2 rounded">
                  <Info className="h-4 w-4" />
                  <span>Les frais de livraison sont définis par chaque producteur</span>
                </div>
              )}
              
              {cartSummary.freeDeliveryItems > 0 && cartSummary.paidDeliveryItems > 0 && (
                <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                  📦 {cartSummary.freeDeliveryItems} produit{cartSummary.freeDeliveryItems > 1 ? 's' : ''} avec livraison gratuite
                  <br />
                  🚚 {cartSummary.paidDeliveryItems} produit{cartSummary.paidDeliveryItems > 1 ? 's' : ''} avec frais de livraison
                </div>
              )}

              {cartSummary.hasNegotiations && (
                <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                  💰 {cartSummary.negotiatedItemsCount} produit{cartSummary.negotiatedItemsCount > 1 ? 's' : ''} au prix négocié
                </div>
              )}
            </div>

            {/* Badge sécurité */}
            <div className="mt-4 p-3 bg-green-50 rounded-md">
              <div className="flex items-center gap-2 text-sm text-green-700">
                <Shield className="h-4 w-4" />
                <span>Commande sécurisée</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;